// src/layers/imagery/resolveImageryStack.js
//
// Pure resolver: turns (stack, cursor, catalog) into RenderSpec[].
//
// Contract:
//   - No I/O, no store reads, no MapLibre, no JWT logic.
//   - Same inputs → same outputs. Safe to memoize on the caller side.
//   - One RenderSpec per stack entry, in stack order (low z first).
//   - Degraded layers are KEPT in the output (visible:false, degraded:reason)
//     so the UI can render the card and show a reason badge.
//
// Snap policy: 'on_or_before'.
//   - Exact match → 0 staleness.
//   - Cursor between two acquisitions → use the older one.
//   - Cursor before earliest → degraded:'before_earliest', visible:false.
//   - Cursor after latest → use latest, staleness = months past it.
//
// Date format: "YYYY_MM" throughout. We never parse to Date objects in the
// hot path — month-level integer math is enough and avoids timezone issues.

/** @typedef {import('./types').Acquisition} Acquisition */
/** @typedef {import('./types').Series} Series */
/** @typedef {import('./types').ImageryCatalog} ImageryCatalog */
/** @typedef {import('./types').ImageryStackEntry} ImageryStackEntry */
/** @typedef {import('./types').Cursor} Cursor */
/** @typedef {import('./types').RenderSpec} RenderSpec */
/** @typedef {import('./types').DegradedReason} DegradedReason */

// ─────────────────────────────────────────────
// Date helpers — "YYYY_MM" as a sortable string,
// converted to an integer month count for arithmetic.
// ─────────────────────────────────────────────

// THE DAY IS OPTIONAL AND IS DELIBERATELY DISCARDED. Staleness is measured in
// MONTHS — that is what stale_threshold_months means and what the UI shows — so
// two acquisitions in one month are equally stale and the day adds nothing to
// this arithmetic. WHICH acquisition a cursor selects is day-precise, and is
// decided by acquisitionOnOrBefore below, not by this function.
//
// WHY THIS IS WRITTEN DOWN: this regex had no day, and dateToMonths returning
// null makes snapToAcquisition report `before_earliest`, which makes
// resolveImageryLayer emit a spec with dataset_key: null, which makes
// syncImageryLayer return BEFORE it ever calls /tile/session. The visible
// symptom is a layer that toggles and requests nothing at all — no tiles, no
// session call, no error, nothing in any log. It cost most of a day on
// 2026-09-02/03.
//
// IT WAS THE THIRD OF THREE INDEPENDENT YYYY_MM REGEXES. The others are
// utils/parseDatasetKey.js and layers/imagery/formatImageryDate.js. A sweep that
// found those two missed this one because it is a bare `const` assignment rather
// than an inline .exec() or .match() — so if a fourth is ever added, add it to
// this list rather than trusting a grep.
const DATE_RE = /^(\d{4})_(\d{2})(?:_(\d{2}))?$/;

/**
 * "2025_06" → 24305, "2025_12_13" → 24311 (months since year 0). The day is
 * accepted and ignored. Returns null for malformed input.
 * @param {string} d
 * @returns {number|null}
 */
export function dateToMonths(d) {
  if (typeof d !== "string") return null;
  const m = DATE_RE.exec(d);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (month < 1 || month > 12) return null;
  return year * 12 + (month - 1);
}

// "2026_03_20" → 20260320. A missing day becomes `missingDay`, which is how a
// month-only date is placed against a dated one. Null for malformed input.
function dayOrdinal(d, missingDay) {
  if (typeof d !== "string") return null;
  const m = DATE_RE.exec(d);
  if (!m) return null;
  const month = parseInt(m[2], 10);
  if (month < 1 || month > 12) return null;
  const day = m[3] != null ? parseInt(m[3], 10) : missingDay;
  return parseInt(m[1], 10) * 10000 + month * 100 + day;
}

/**
 * THE on-or-before test, shared by every caller that snaps a cursor to an
 * acquisition. Day-precise where both sides carry a day. A month cursor
 * ("2026_06", the default) means the whole month, so it reaches an acquisition
 * made inside it; a month-only acquisition counts from the 1st. Null-safe:
 * malformed input never matches.
 * @param {string} acquisitionDate
 * @param {string} cursorDate
 * @returns {boolean}
 */
export function acquisitionOnOrBefore(acquisitionDate, cursorDate) {
  const a = dayOrdinal(acquisitionDate, 1);
  const c = dayOrdinal(cursorDate, 31);
  return a != null && c != null && a <= c;
}

/**
 * Inclusive month diff: |a - b|. Returns null if either is invalid.
 * @param {string} a
 * @param {string} b
 */
export function monthsBetween(a, b) {
  const am = dateToMonths(a);
  const bm = dateToMonths(b);
  if (am == null || bm == null) return null;
  return Math.abs(am - bm);
}

// ─────────────────────────────────────────────
// Entitlement check
// ─────────────────────────────────────────────

/**
 * Returns null if entitled and active. Otherwise the degraded reason.
 * @param {Series} series
 * @param {Date} [now=new Date()]
 * @returns {DegradedReason|null}
 */
export function checkEntitlement(series, now = new Date()) {
  const ent = series.entitlement;
  if (!ent) return "not_entitled";
  if (ent.status === "revoked") return "not_entitled";
  if (ent.status === "expired") return "expired";
  // Belt-and-suspenders: even if status says active, verify against now.
  // resolve_effective_entitlement should already filter, but the catalog
  // payload may be stale (we cache it client-side).
  if (ent.valid_to && new Date(ent.valid_to) <= now) return "expired";
  if (ent.valid_from && new Date(ent.valid_from) > now) return "not_entitled";
  return null;
}

// ─────────────────────────────────────────────
// Snap logic
// ─────────────────────────────────────────────

/**
 * Pick the acquisition for a cursor date using on-or-before policy.
 * Acquisitions MUST be pre-sorted ascending by acquisition_date.
 *
 * Returns:
 *   { acquisition, staleness_months, degraded: null }  on success
 *   { acquisition: null, staleness_months: 0, degraded: 'before_earliest' }
 *   { acquisition: null, staleness_months: 0, degraded: 'no_acquisitions' }
 *
 * Cursor AFTER latest returns the latest with positive staleness (not degraded).
 *
 * @param {string} cursorDate
 * @param {Acquisition[]} acquisitions
 * @returns {{ acquisition: Acquisition|null, staleness_months: number, degraded: DegradedReason|null }}
 */
export function snapToAcquisition(cursorDate, acquisitions) {
  if (!acquisitions || acquisitions.length === 0) {
    return { acquisition: null, staleness_months: 0, degraded: "no_acquisitions" };
  }

  const cursorM = dateToMonths(cursorDate);
  if (cursorM == null) {
    // Bad cursor → treat as "before earliest" rather than crash. The UI
    // shouldn't be able to produce this, but defense-in-depth.
    return { acquisition: null, staleness_months: 0, degraded: "before_earliest" };
  }

  // Binary search would be nicer but acquisitions are small (tens, not thousands).
  // Walk from the end and find the latest acquisition <= cursor.
  let chosen = null;
  for (let i = acquisitions.length - 1; i >= 0; i--) {
    if (acquisitionOnOrBefore(acquisitions[i].acquisition_date, cursorDate)) {
      chosen = acquisitions[i];             // malformed dates never match
      break;
    }
  }

  if (!chosen) {
    return { acquisition: null, staleness_months: 0, degraded: "before_earliest" };
  }

  const stalenessM = cursorM - dateToMonths(chosen.acquisition_date);
  return { acquisition: chosen, staleness_months: stalenessM, degraded: null };
}

// ─────────────────────────────────────────────
// Per-layer resolution
// ─────────────────────────────────────────────

/**
 * Resolve a single stack entry into a RenderSpec.
 * @param {ImageryStackEntry} entry
 * @param {Cursor} cursor
 * @param {Series|undefined} series   Looked up by entry.series_key; undefined if missing
 * @param {Date} [now]
 * @returns {RenderSpec}
 */
export function resolveImageryLayer(entry, cursor, series, now = new Date()) {
  // Base spec carries everything the UI needs even when degraded.
  const base = {
    layer_id: entry.layer_id,
    series_key: entry.series_key,
    dataset_key: null,
    effective_date: null,
    cursor_date: entry.time_mode === "pinned" ? entry.pinned_date : cursor.global_date,
    staleness_months: 0,
    is_stale: false,
    scope: null,
    aoi_id: null,
    opacity: entry.opacity,
    // Per-layer raster adjustments, carried verbatim like opacity. The store is
    // the clamp authority; the resolver just forwards entry → spec → registry.
    // Identity fallback tolerates legacy entries that predate the field.
    adjust: entry.adjust ?? { brightnessMin: 0, contrast: 0, saturation: 0, hueRotate: 0 },
    visible: false,             // flipped to true at the end on success
    z_index: entry.z_index,
    degraded: null,
    display_name: series?.display_name ?? entry.series_key,
    basin: series?.basin ?? "",
    region: series?.region ?? "",
    sku_code: series?.sku_code ?? "",
  };

  // Missing series in catalog — either client cache is stale or entry is orphaned.
  // Don't crash; mark not_entitled so the UI shows a clear reason.
  if (!series) {
    return { ...base, degraded: "not_entitled" };
  }

  // Entitlement gate — checked BEFORE snap, since an expired layer
  // shouldn't waste cycles picking a date we'll never render.
  const entReason = checkEntitlement(series, now);
  if (entReason) {
    return { ...base, degraded: entReason };
  }

  // Determine which date to snap against.
  let cursorDate;
  if (entry.time_mode === "pinned") {
    if (!entry.pinned_date) {
      return { ...base, degraded: "invalid_pinned_date" };
    }
    cursorDate = entry.pinned_date;
  } else {
    cursorDate = cursor.global_date;
  }

  const { acquisition, staleness_months, degraded } = snapToAcquisition(
    cursorDate,
    series.acquisitions
  );

  if (degraded || !acquisition) {
    return { ...base, degraded: degraded ?? "no_acquisitions" };
  }

  const ent = /** @type {NonNullable<typeof series.entitlement>} */ (series.entitlement);

  return {
    ...base,
    dataset_key: acquisition.dataset_key,
    effective_date: acquisition.acquisition_date,
    staleness_months,
    is_stale: staleness_months > cursor.stale_threshold_months,
    scope: ent.scope,
    aoi_id: ent.scope === "aoi" ? ent.coverage_aoi_id : null,
    visible: entry.visible,    // honor user's per-layer visibility
  };
}

// ─────────────────────────────────────────────
// Top-level resolver
// ─────────────────────────────────────────────

/**
 * @param {{ stack: ImageryStackEntry[], cursor: Cursor, catalog: ImageryCatalog }} args
 * @param {Date} [now]
 * @returns {RenderSpec[]}
 */
export function resolveImageryStack({ stack, cursor, catalog }, now = new Date()) {
  // Build a series_key → Series index once per call. The catalog can be
  // large-ish (hundreds of series across an org), but stack is small (tens),
  // so we accept the catalog scan cost in exchange for a fast inner loop.
  /** @type {Record<string, Series>} */
  const seriesIndex = {};
  for (const s of catalog.series) seriesIndex[s.series_key] = s;

  // Render order: sort by z_index ascending so callers can append straight
  // into MapLibre (which renders later-added layers on top).
  const sorted = [...stack].sort((a, b) => a.z_index - b.z_index);

  return sorted.map((entry) =>
    resolveImageryLayer(entry, cursor, seriesIndex[entry.series_key], now)
  );
}

// ─────────────────────────────────────────────
// Helper: build series_key from raw fields. Single source of truth so
// catalog construction (footprints → series) and stack construction
// (user click → entry) agree on identity.
// ─────────────────────────────────────────────

/**
 * @param {string} slug
 * @param {string} sku_code
 * @returns {string}
 */
export function makeSeriesKey(slug, sku_code) {
  return `${slug}::${sku_code}`;
}